import api from "./api";

const DEVICE_SECRET_KEY = "computer_device_secret";

// ==========================================================
// DEVICE SECRET
// ==========================================================

const getDeviceSecret = () => {
  let secret = localStorage.getItem(
    DEVICE_SECRET_KEY
  );

  if (!secret) {
    const bytes = new Uint8Array(32);

    window.crypto.getRandomValues(bytes);

    secret = Array.from(bytes)
      .map((byte) =>
        byte.toString(16).padStart(2, "0")
      )
      .join("");

    localStorage.setItem(
      DEVICE_SECRET_KEY,
      secret
    );
  }

  return secret;
};

// ==========================================================
// GET COMPUTER TOKEN
// ==========================================================

const getToken = () => {
  return (
    sessionStorage.getItem("computer_token") ||
    localStorage.getItem("computer_token")
  );
};

// ==========================================================
// GET STORED COMPUTER
// ==========================================================

const getComputer = () => {
  try {
    const stored =
      sessionStorage.getItem("computer") ||
      localStorage.getItem("computer");

    return stored
      ? JSON.parse(stored)
      : null;
  } catch (error) {
    console.error(
      "Invalid Computer Data:",
      error
    );

    return null;
  }
};

// ==========================================================
// COMPUTER LOGIN
// ==========================================================

const login = async (
  computerCode,
  password
) => {
  const deviceSecret =
    getDeviceSecret();

  const response = await api.post(
    "/computer/login",
    {
      computer_code: String(
        computerCode || ""
      )
        .trim()
        .toUpperCase(),

      password: String(
        password || ""
      ),

      device_secret: deviceSecret,
    }
  );

  return response.data;
};

// ==========================================================
// GET LATEST COMPUTER PROFILE
// ==========================================================

const getProfile = async () => {
  const token = getToken();

  if (!token) {
    throw new Error(
      "Computer session not found."
    );
  }

  const response = await api.get(
    "/computer/me",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const data = response.data;

  // Keep latest Computer information
  // synchronized with sessionStorage.
  if (
    data?.success &&
    data?.computer
  ) {
    sessionStorage.setItem(
      "computer",
      JSON.stringify(
        data.computer
      )
    );
    localStorage.setItem(
      "computer",
      JSON.stringify(
        data.computer
      )
    );
  }

  return data;
};

// ==========================================================
// GET COMPUTER HISTORY
// ==========================================================

const getHistory = async () => {
  const token = getToken();

  if (!token) {
    throw new Error(
      "Computer session not found."
    );
  }

  const response = await api.get(
    "/computer/history",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

// ==========================================================
// ADD COMPUTER GAME ACTIVITY
// ==========================================================

const addActivity = async (
  activityType,
  note = ""
) => {
  const token = getToken();

  if (!token) {
    throw new Error(
      "Computer session not found."
    );
  }

  const response = await api.post(
    "/computer/activity",
    {
      activity_type: String(
        activityType || ""
      )
        .trim()
        .toUpperCase(),

      note: String(
        note || ""
      ).trim(),
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

// ==========================================================
// COMPUTER LOGOUT
// ==========================================================

const logout = () => {
  sessionStorage.removeItem("computer_token");
  sessionStorage.removeItem("computer");
  sessionStorage.removeItem("pc_disclaimer_accepted");
  localStorage.removeItem("computer_token");
  localStorage.removeItem("computer");

  // IMPORTANT:
  // computer_device_secret ko remove nahi karna.
  //
  // Isi secret ki wajah se Computer ID
  // registered browser/device ke saath bound
  // rehti hai.
};

// ==========================================================
// CHECK COMPUTER SESSION
// ==========================================================

const isLoggedIn = () => {
  const token = getToken();
  const computer = getComputer();

  return Boolean(
    token && computer
  );
};

const takeAllCoins = async (amount) => {
  const token = getToken() || localStorage.getItem("token");

  const response = await api.post(
    "/computer/take-coins",
    { amount: Number(amount) },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );

  return response.data;
};

// ==========================================================
// EXPORT SERVICE
// ==========================================================

const computerAuthService = {
  login,
  logout,

  getProfile,
  getHistory,

  // Safe game/account activity logging
  addActivity,

  takeAllCoins,
  takeCoins: takeAllCoins,

  getToken,
  getComputer,
  getDeviceSecret,
  isLoggedIn,
};

export default computerAuthService;